import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { 
  FileText, 
  MessageSquare, 
  UserPlus, 
  FolderOpen,
  GitBranch,
  ArrowRight,
  ArrowLeft,
  Check,
  Clock,
  Shield,
  Users,
  Zap,
  Sparkles,
  DollarSign,
  Timer,
  Globe,
  Monitor,
  Chrome,
  Brain,
  Gavel,
  Target,
  TrendingUp,
  BarChart3,
  Mail,
  Phone,
  Calendar,
  CreditCard,
  Building,
  Briefcase,
  BookOpen,
  Search,
  Upload,
  Download,
  Share2,
  Lock,
  Eye,
  FileCheck,
  Workflow,
  PenTool,
  Receipt,
  Banknote,
  ClipboardCheck,
  MessageCircle,
  Video,
  Mic,
  FileSignature,
  Settings,
  Layers,
  Database,
  CloudUpload
} from 'lucide-react';

export default function AboutPage() {
  return (
    <div className="min-h-screen bg-background" data-testid="about-page">
      {/* Header */}
      <header className="sticky top-0 z-50 w-full border-b bg-white/95 backdrop-blur">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-3">
              <img src="/logo.png" alt="Legal AIDynamics" className="w-10 h-10 rounded-lg object-contain" />
              <div>
                <span className="font-heading text-xl font-bold text-navy">Legal AI</span>
                <span className="font-heading text-xl font-bold text-bronze">Dynamics</span>
              </div>
            </Link>
            
            <nav className="hidden md:flex items-center gap-6">
              <Link to="/" className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors">
                Home
              </Link>
              <a href="#products" className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors">
                Products
              </a>
              <a href="#integrations" className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors">
                Integrations
              </a>
              <a href="#security" className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors">
                Security
              </a>
            </nav>

            <div className="flex items-center gap-3">
              <Link to="/login">
                <Button variant="ghost" className="hidden sm:flex">Sign In</Button>
              </Link>
              <Link to="/register">
                <Button className="bg-navy hover:bg-navy-light">
                  Start Free Trial
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-to-b from-navy to-navy-light py-20 lg:py-28">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-10 left-10 w-64 h-64 border border-white rounded-full" />
          <div className="absolute top-32 right-16 w-80 h-80 border border-white rounded-full" />
          <div className="absolute bottom-10 left-1/4 w-40 h-40 border border-white rounded-full" />
        </div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <Link to="/" className="inline-flex items-center text-white/80 hover:text-white mb-8 transition-colors">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Home
          </Link>
          
          <div className="max-w-4xl">
            <Badge variant="outline" className="mb-6 px-4 py-1.5 border-white/30 text-white">
              <Sparkles className="w-4 h-4 mr-2 text-bronze" />
              About Legal AIDynamics
            </Badge>
            
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
              The Complete AI-Powered
              <span className="block text-bronze">Legal Practice Suite</span>
            </h1>
            
            <p className="text-lg sm:text-xl text-white/80 mb-8 max-w-2xl">
              Legal AIDynamics combines cutting-edge artificial intelligence with deep legal expertise to deliver 
              a comprehensive platform that transforms how law firms manage cases, clients, documents, and billing.
            </p>

            <div className="flex flex-wrap gap-4">
              <div className="flex items-center gap-2 text-white/90">
                <Check className="w-5 h-5 text-bronze" />
                <span>6 Integrated Products</span>
              </div>
              <div className="flex items-center gap-2 text-white/90">
                <Check className="w-5 h-5 text-bronze" />
                <span>AI-Powered Throughout</span>
              </div>
              <div className="flex items-center gap-2 text-white/90">
                <Check className="w-5 h-5 text-bronze" />
                <span>Built for Litigators</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Mission Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="font-heading text-3xl sm:text-4xl font-bold text-navy mb-6">
                Our Mission
              </h2>
              <p className="text-lg text-muted-foreground mb-6">
                We believe that attorneys should spend their time on what matters most: advocating for their clients 
                and winning cases. That's why we built Legal AIDynamics, to handle the time-consuming administrative 
                tasks that drain productivity and inflate costs.
              </p>
              <p className="text-lg text-muted-foreground mb-6">
                Our suite of AI-powered tools automates document review, streamlines client intake, manages billing 
                and trust accounting, and helps prepare for trial, all while maintaining the highest standards of 
                security and compliance.
              </p>
              <div className="grid grid-cols-2 gap-4">
                <div className="p-4 bg-slate-50 rounded-lg">
                  <div className="text-3xl font-bold text-bronze mb-1">10+</div>
                  <div className="text-sm text-muted-foreground">Hours saved per case</div>
                </div>
                <div className="p-4 bg-slate-50 rounded-lg">
                  <div className="text-3xl font-bold text-bronze mb-1">99.9%</div>
                  <div className="text-sm text-muted-foreground">Uptime guarantee</div>
                </div>
              </div>
            </div>
            <div className="relative">
              <div className="aspect-square bg-gradient-to-br from-slate-100 to-slate-200 rounded-2xl flex items-center justify-center">
                <div className="grid grid-cols-2 gap-4 p-8">
                  <div className="bg-white p-6 rounded-xl shadow-lg">
                    <Brain className="w-10 h-10 text-bronze mb-3" />
                    <div className="font-semibold">AI Analysis</div>
                  </div>
                  <div className="bg-white p-6 rounded-xl shadow-lg">
                    <FileText className="w-10 h-10 text-blue-600 mb-3" />
                    <div className="font-semibold">Documents</div>
                  </div>
                  <div className="bg-white p-6 rounded-xl shadow-lg">
                    <DollarSign className="w-10 h-10 text-emerald-600 mb-3" />
                    <div className="font-semibold">Billing</div>
                  </div>
                  <div className="bg-white p-6 rounded-xl shadow-lg">
                    <Users className="w-10 h-10 text-violet-600 mb-3" />
                    <div className="font-semibold">Clients</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Products Detail Section */}
      <section id="products" className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <Badge variant="outline" className="mb-4">
              <Layers className="w-4 h-4 mr-2" />
              Complete Product Suite
            </Badge>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold text-navy mb-4">
              Six Powerful Products, One Platform
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Each product is designed to solve specific challenges in legal practice, and they all work 
              together seamlessly to create a unified workflow.
            </p>
          </div>

          {/* Product 1: Exhibit List.AI */}
          <div className="mb-16">
            <Card className="overflow-hidden border-bronze/30">
              <div className="grid lg:grid-cols-2">
                <div className="p-8 lg:p-12">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-14 h-14 rounded-xl bg-bronze flex items-center justify-center">
                      <FileText className="w-7 h-7 text-white" />
                    </div>
                    <div>
                      <h3 className="font-heading text-2xl font-bold">Exhibit List.AI</h3>
                      <Badge className="bg-green-500 mt-1">LIVE</Badge>
                    </div>
                  </div>
                  <p className="text-muted-foreground mb-6">
                    Transform thousands of documents into organized, court-ready exhibits with AI-powered 
                    identification and labeling. Our system analyzes document content to suggest exhibit 
                    classifications and generates professional PDF stamps.
                  </p>
                  <div className="space-y-3 mb-6">
                    <div className="flex items-start gap-3">
                      <Upload className="w-5 h-5 text-bronze mt-0.5" />
                      <div>
                        <div className="font-medium">Bulk Document Upload</div>
                        <div className="text-sm text-muted-foreground">Upload thousands of documents at once with drag-and-drop</div>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <Brain className="w-5 h-5 text-bronze mt-0.5" />
                      <div>
                        <div className="font-medium">AI Exhibit Identification</div>
                        <div className="text-sm text-muted-foreground">Automatic categorization and exhibit number suggestions</div>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <FileCheck className="w-5 h-5 text-bronze mt-0.5" />
                      <div>
                        <div className="font-medium">PDF Label Stamping</div>
                        <div className="text-sm text-muted-foreground">Professional exhibit labels applied directly to PDFs</div>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <Download className="w-5 h-5 text-bronze mt-0.5" />
                      <div>
                        <div className="font-medium">Court-Ready Reports</div>
                        <div className="text-sm text-muted-foreground">Generate exhibit lists in multiple formats</div>
                      </div>
                    </div>
                  </div>
                  <div className="text-2xl font-bold text-bronze">$20/month</div>
                </div>
                <div className="bg-gradient-to-br from-bronze/10 to-bronze/5 p-8 lg:p-12 flex items-center justify-center">
                  <div className="text-center">
                    <div className="text-6xl font-bold text-bronze/20 mb-2">AI</div>
                    <div className="text-lg text-muted-foreground">Exhibit Management</div>
                  </div>
                </div>
              </div>
            </Card>
          </div>

          {/* Product 2: Examination.AI */}
          <div className="mb-16">
            <Card className="overflow-hidden border-purple-200">
              <div className="grid lg:grid-cols-2">
                <div className="bg-gradient-to-br from-purple-50 to-purple-100/50 p-8 lg:p-12 flex items-center justify-center order-2 lg:order-1">
                  <div className="text-center">
                    <Gavel className="w-20 h-20 text-purple-300 mx-auto mb-4" />
                    <div className="text-lg text-muted-foreground">Trial Preparation</div>
                  </div>
                </div>
                <div className="p-8 lg:p-12 order-1 lg:order-2">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-14 h-14 rounded-xl bg-purple-600 flex items-center justify-center">
                      <MessageSquare className="w-7 h-7 text-white" />
                    </div>
                    <div>
                      <h3 className="font-heading text-2xl font-bold">Examination.AI</h3>
                      <Badge className="bg-green-500 mt-1">LIVE</Badge>
                    </div>
                  </div>
                  <p className="text-muted-foreground mb-6">
                    Generate comprehensive witness examination outlines powered by Claude Sonnet 4.5. 
                    Analyze case documents and exhibits to create structured questions for direct examination, 
                    cross-examination, and opening/closing arguments.
                  </p>
                  <div className="space-y-3 mb-6">
                    <div className="flex items-start gap-3">
                      <Target className="w-5 h-5 text-purple-600 mt-0.5" />
                      <div>
                        <div className="font-medium">Direct Examination Outlines</div>
                        <div className="text-sm text-muted-foreground">Open-ended questions with anticipated answers</div>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <Gavel className="w-5 h-5 text-purple-600 mt-0.5" />
                      <div>
                        <div className="font-medium">Cross-Examination Questions</div>
                        <div className="text-sm text-muted-foreground">Leading questions to control witness testimony</div>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <BookOpen className="w-5 h-5 text-purple-600 mt-0.5" />
                      <div>
                        <div className="font-medium">Opening & Closing Arguments</div>
                        <div className="text-sm text-muted-foreground">Structured outlines with theme development</div>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <Database className="w-5 h-5 text-purple-600 mt-0.5" />
                      <div>
                        <div className="font-medium">Case Integration</div>
                        <div className="text-sm text-muted-foreground">Pulls from Case Management and Exhibit List</div>
                      </div>
                    </div>
                  </div>
                  <div className="text-2xl font-bold text-purple-600">Included with Suite</div>
                </div>
              </div>
            </Card>
          </div>

          {/* Product 3: Billing.AI */}
          <div className="mb-16">
            <Card className="overflow-hidden border-emerald-200">
              <div className="grid lg:grid-cols-2">
                <div className="p-8 lg:p-12">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-14 h-14 rounded-xl bg-emerald-600 flex items-center justify-center">
                      <DollarSign className="w-7 h-7 text-white" />
                    </div>
                    <div>
                      <h3 className="font-heading text-2xl font-bold">Billing.AI</h3>
                      <Badge className="bg-green-500 mt-1">LIVE</Badge>
                    </div>
                  </div>
                  <p className="text-muted-foreground mb-6">
                    Complete legal billing solution with IOLTA trust accounting, AI-powered billing review, 
                    and multi-processor payment integration. Track time, generate invoices, and maintain 
                    bar-compliant trust accounting records.
                  </p>
                  <div className="space-y-3 mb-6">
                    <div className="flex items-start gap-3">
                      <Banknote className="w-5 h-5 text-emerald-600 mt-0.5" />
                      <div>
                        <div className="font-medium">IOLTA Trust Accounting</div>
                        <div className="text-sm text-muted-foreground">Bar-compliant trust and operating accounts</div>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <Brain className="w-5 h-5 text-emerald-600 mt-0.5" />
                      <div>
                        <div className="font-medium">AI Billing Error Detection</div>
                        <div className="text-sm text-muted-foreground">Automatic detection of billing anomalies</div>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <CreditCard className="w-5 h-5 text-emerald-600 mt-0.5" />
                      <div>
                        <div className="font-medium">Multi-Processor Payments</div>
                        <div className="text-sm text-muted-foreground">Stripe, PayPal, LawPay, and Affirm integration</div>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <Receipt className="w-5 h-5 text-emerald-600 mt-0.5" />
                      <div>
                        <div className="font-medium">Monthly Reconciliation</div>
                        <div className="text-sm text-muted-foreground">Three-way trust reconciliation reports</div>
                      </div>
                    </div>
                  </div>
                  <div className="text-2xl font-bold text-emerald-600">$25/month</div>
                </div>
                <div className="bg-gradient-to-br from-emerald-50 to-emerald-100/50 p-8 lg:p-12 flex items-center justify-center">
                  <div className="text-center">
                    <BarChart3 className="w-20 h-20 text-emerald-300 mx-auto mb-4" />
                    <div className="text-lg text-muted-foreground">Financial Management</div>
                  </div>
                </div>
              </div>
            </Card>
          </div>

          {/* Product 4: Intake.AI */}
          <div className="mb-16">
            <Card className="overflow-hidden border-blue-200">
              <div className="grid lg:grid-cols-2">
                <div className="bg-gradient-to-br from-blue-50 to-blue-100/50 p-8 lg:p-12 flex items-center justify-center order-2 lg:order-1">
                  <div className="text-center">
                    <UserPlus className="w-20 h-20 text-blue-300 mx-auto mb-4" />
                    <div className="text-lg text-muted-foreground">Client Acquisition</div>
                  </div>
                </div>
                <div className="p-8 lg:p-12 order-1 lg:order-2">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-14 h-14 rounded-xl bg-blue-600 flex items-center justify-center">
                      <UserPlus className="w-7 h-7 text-white" />
                    </div>
                    <div>
                      <h3 className="font-heading text-2xl font-bold">Intake.AI</h3>
                      <Badge className="bg-green-500 mt-1">LIVE</Badge>
                    </div>
                  </div>
                  <p className="text-muted-foreground mb-6">
                    Streamline your client intake process with customizable forms, lead pipeline management, 
                    AI-powered consultation recording with Whisper transcription, and e-signature agreements. 
                    Never lose a lead again.
                  </p>
                  <div className="space-y-3 mb-6">
                    <div className="flex items-start gap-3">
                      <Workflow className="w-5 h-5 text-blue-600 mt-0.5" />
                      <div>
                        <div className="font-medium">Kanban Lead Pipeline</div>
                        <div className="text-sm text-muted-foreground">Visual lead tracking with customizable stages</div>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <Mic className="w-5 h-5 text-blue-600 mt-0.5" />
                      <div>
                        <div className="font-medium">AI Consultation Recording</div>
                        <div className="text-sm text-muted-foreground">Record and transcribe client consultations</div>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <FileSignature className="w-5 h-5 text-blue-600 mt-0.5" />
                      <div>
                        <div className="font-medium">E-Signature Agreements</div>
                        <div className="text-sm text-muted-foreground">Send and track engagement letters</div>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <MessageCircle className="w-5 h-5 text-blue-600 mt-0.5" />
                      <div>
                        <div className="font-medium">SMS Communication</div>
                        <div className="text-sm text-muted-foreground">Twilio-powered text messaging with leads</div>
                      </div>
                    </div>
                  </div>
                  <div className="text-2xl font-bold text-blue-600">$20/month</div>
                </div>
              </div>
            </Card>
          </div>

          {/* Product 5: Case Management.AI */}
          <div className="mb-16">
            <Card className="overflow-hidden border-indigo-200">
              <div className="grid lg:grid-cols-2">
                <div className="p-8 lg:p-12">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-14 h-14 rounded-xl bg-indigo-600 flex items-center justify-center">
                      <FolderOpen className="w-7 h-7 text-white" />
                    </div>
                    <div>
                      <h3 className="font-heading text-2xl font-bold">Case Management.AI</h3>
                      <Badge className="bg-green-500 mt-1">LIVE</Badge>
                    </div>
                  </div>
                  <p className="text-muted-foreground mb-6">
                    Comprehensive case and matter management with custom fields, document templates with merge 
                    fields, time tracking, client communication logging, and team collaboration. Everything 
                    you need to manage your caseload.
                  </p>
                  <div className="space-y-3 mb-6">
                    <div className="flex items-start gap-3">
                      <Settings className="w-5 h-5 text-indigo-600 mt-0.5" />
                      <div>
                        <div className="font-medium">Custom Fields & Templates</div>
                        <div className="text-sm text-muted-foreground">Create matter templates for case types</div>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <FileText className="w-5 h-5 text-indigo-600 mt-0.5" />
                      <div>
                        <div className="font-medium">Document Management</div>
                        <div className="text-sm text-muted-foreground">Upload, organize, and merge documents</div>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <Clock className="w-5 h-5 text-indigo-600 mt-0.5" />
                      <div>
                        <div className="font-medium">Time Tracking</div>
                        <div className="text-sm text-muted-foreground">Track billable time with AI suggestions</div>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <Mail className="w-5 h-5 text-indigo-600 mt-0.5" />
                      <div>
                        <div className="font-medium">Email Integration</div>
                        <div className="text-sm text-muted-foreground">Outlook and Gmail email filing</div>
                      </div>
                    </div>
                  </div>
                  <div className="text-2xl font-bold text-indigo-600">$30/month</div>
                </div>
                <div className="bg-gradient-to-br from-indigo-50 to-indigo-100/50 p-8 lg:p-12 flex items-center justify-center">
                  <div className="text-center">
                    <Briefcase className="w-20 h-20 text-indigo-300 mx-auto mb-4" />
                    <div className="text-lg text-muted-foreground">Matter Management</div>
                  </div>
                </div>
              </div>
            </Card>
          </div>

          {/* Product 6: Workflow.AI */}
          <div className="mb-8">
            <Card className="overflow-hidden border-violet-200">
              <div className="grid lg:grid-cols-2">
                <div className="bg-gradient-to-br from-violet-50 to-violet-100/50 p-8 lg:p-12 flex items-center justify-center order-2 lg:order-1">
                  <div className="text-center">
                    <GitBranch className="w-20 h-20 text-violet-300 mx-auto mb-4" />
                    <div className="text-lg text-muted-foreground">Process Automation</div>
                  </div>
                </div>
                <div className="p-8 lg:p-12 order-1 lg:order-2">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-14 h-14 rounded-xl bg-violet-600 flex items-center justify-center">
                      <GitBranch className="w-7 h-7 text-white" />
                    </div>
                    <div>
                      <h3 className="font-heading text-2xl font-bold">Workflow.AI</h3>
                      <Badge className="bg-green-500 mt-1">LIVE</Badge>
                    </div>
                  </div>
                  <p className="text-muted-foreground mb-6">
                    Transform office procedures into visual workflow charts with AI. Simply describe your 
                    process in plain language, and our AI generates professional workflow diagrams with 
                    automation recommendations.
                  </p>
                  <div className="space-y-3 mb-6">
                    <div className="flex items-start gap-3">
                      <PenTool className="w-5 h-5 text-violet-600 mt-0.5" />
                      <div>
                        <div className="font-medium">Natural Language Input</div>
                        <div className="text-sm text-muted-foreground">Describe procedures in plain text</div>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <Brain className="w-5 h-5 text-violet-600 mt-0.5" />
                      <div>
                        <div className="font-medium">AI-Generated Workflows</div>
                        <div className="text-sm text-muted-foreground">Automatic flowchart generation</div>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <Zap className="w-5 h-5 text-violet-600 mt-0.5" />
                      <div>
                        <div className="font-medium">Automation Suggestions</div>
                        <div className="text-sm text-muted-foreground">AI identifies automation opportunities</div>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <Share2 className="w-5 h-5 text-violet-600 mt-0.5" />
                      <div>
                        <div className="font-medium">Team Sharing</div>
                        <div className="text-sm text-muted-foreground">Share workflows with role-based access</div>
                      </div>
                    </div>
                  </div>
                  <div className="text-2xl font-bold text-violet-600">$20/month</div>
                </div>
              </div>
            </Card>
          </div>
        </div>
      </section>

      {/* Time Tracking Feature */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <Badge variant="outline" className="mb-4">
              <Timer className="w-4 h-4 mr-2" />
              Advanced Feature
            </Badge>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold text-navy mb-4">
              AI-Powered Time Tracking
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Never miss billable time again. Our intelligent time tracking monitors your work across 
              the entire Legal AIDynamics platform and external applications.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <Card className="text-center">
              <CardContent className="pt-8">
                <div className="w-16 h-16 rounded-full bg-bronze/10 flex items-center justify-center mx-auto mb-4">
                  <Timer className="w-8 h-8 text-bronze" />
                </div>
                <h3 className="font-heading text-xl font-semibold mb-3">In-App Tracking</h3>
                <p className="text-muted-foreground text-sm">
                  Automatically tracks time spent on documents, cases, communications, and AI analysis 
                  within Legal AIDynamics. AI generates billing descriptions with confidence scores.
                </p>
              </CardContent>
            </Card>

            <Card className="text-center">
              <CardContent className="pt-8">
                <div className="w-16 h-16 rounded-full bg-blue-100 flex items-center justify-center mx-auto mb-4">
                  <Monitor className="w-8 h-8 text-blue-600" />
                </div>
                <h3 className="font-heading text-xl font-semibold mb-3">Microsoft 365 Integration</h3>
                <p className="text-muted-foreground text-sm">
                  Track time in Word, Excel, PowerPoint, Outlook, and Teams. Connect your Microsoft account 
                  to capture work done outside the platform.
                </p>
              </CardContent>
            </Card>

            <Card className="text-center">
              <CardContent className="pt-8">
                <div className="w-16 h-16 rounded-full bg-amber-100 flex items-center justify-center mx-auto mb-4">
                  <Chrome className="w-8 h-8 text-amber-600" />
                </div>
                <h3 className="font-heading text-xl font-semibold mb-3">Browser Extension</h3>
                <p className="text-muted-foreground text-sm">
                  Track time on legal research sites like Westlaw, LexisNexis, and PACER. Available for 
                  Chrome and Firefox with privacy-focused design.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Integrations Section */}
      <section id="integrations" className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <Badge variant="outline" className="mb-4">
              <Globe className="w-4 h-4 mr-2" />
              Integrations
            </Badge>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold text-navy mb-4">
              Connects With Your Existing Tools
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Legal AIDynamics integrates with the tools you already use, creating a seamless workflow 
              across your practice.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { name: 'Microsoft Outlook', desc: 'Email filing to cases', icon: Mail },
              { name: 'Gmail', desc: 'Email filing to cases', icon: Mail },
              { name: 'Twilio SMS', desc: 'Client text messaging', icon: Phone },
              { name: 'OpenAI Whisper', desc: 'Audio transcription', icon: Mic },
              { name: 'Claude AI', desc: 'Document analysis', icon: Brain },
              { name: 'Stripe', desc: 'Payment processing', icon: CreditCard },
              { name: 'PayPal', desc: 'Payment processing', icon: CreditCard },
              { name: 'Microsoft 365', desc: 'Time tracking', icon: Monitor },
              { name: 'Google Calendar', desc: 'Scheduling', icon: Calendar },
              { name: 'Westlaw', desc: 'Research tracking', icon: Search },
              { name: 'LexisNexis', desc: 'Research tracking', icon: Search },
              { name: 'PACER', desc: 'Court filings', icon: Building }
            ].map((integration, i) => (
              <Card key={i} className="bg-white">
                <CardContent className="p-4 flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center flex-shrink-0">
                    <integration.icon className="w-5 h-5 text-slate-600" />
                  </div>
                  <div>
                    <div className="font-medium text-sm">{integration.name}</div>
                    <div className="text-xs text-muted-foreground">{integration.desc}</div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Security Section */}
      <section id="security" className="py-20 bg-navy text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <Badge variant="outline" className="mb-4 border-white/30 text-white">
              <Shield className="w-4 h-4 mr-2" />
              Security & Compliance
            </Badge>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold mb-4">
              Your Data is Protected
            </h2>
            <p className="text-lg text-white/70 max-w-2xl mx-auto">
              We understand the sensitive nature of legal data. That's why we've built Legal AIDynamics 
              with security and compliance as foundational principles.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: Lock, title: 'End-to-End Encryption', desc: 'All data encrypted at rest and in transit' },
              { icon: Shield, title: 'SOC 2 Compliant', desc: 'Enterprise security standards' },
              { icon: Eye, title: 'Access Controls', desc: 'Role-based permissions' },
              { icon: CloudUpload, title: '99.9% Uptime', desc: 'Reliable cloud infrastructure' }
            ].map((item, i) => (
              <div key={i} className="text-center">
                <div className="w-16 h-16 rounded-full bg-white/10 flex items-center justify-center mx-auto mb-4">
                  <item.icon className="w-8 h-8 text-bronze" />
                </div>
                <h3 className="font-semibold mb-2">{item.title}</h3>
                <p className="text-sm text-white/70">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-bronze to-bronze-light">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading text-3xl sm:text-4xl font-bold text-white mb-6">
            Ready to Transform Your Practice?
          </h2>
          <p className="text-lg text-white/90 mb-8 max-w-2xl mx-auto">
            Join thousands of attorneys who are saving time, reducing costs, and winning more cases 
            with Legal AIDynamics.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link to="/register">
              <Button size="lg" className="bg-white text-bronze hover:bg-white/90 text-base px-8">
                Start Free 7-Day Trial
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
            <Link to="/">
              <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10 text-base px-8">
                Back to Home
              </Button>
            </Link>
          </div>
          <p className="text-sm text-white/70 mt-6">
            No credit card required • Cancel anytime
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-3">
              <img src="/logo.png" alt="Legal AIDynamics" className="w-10 h-10 rounded-lg object-contain" />
              <div>
                <span className="font-heading text-xl font-bold text-white">Legal AI</span>
                <span className="font-heading text-xl font-bold text-bronze">Dynamics</span>
              </div>
            </div>
            
            <div className="flex items-center gap-6 text-sm text-white/60">
              <Link to="/terms" className="hover:text-white transition-colors">Terms of Service</Link>
              <Link to="/" className="hover:text-white transition-colors">Privacy Policy</Link>
              <span>© 2026 Legal AIDynamics, LLC. All rights reserved.</span>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
